/**
 * Establishment autocomplete labels and the shared search-field hint.
 */

import type { Establishment } from '@/types/establishment.types';

/** INSEE-style dept code from French postal (5 digits). DOM/TOM: first 3 digits; Corse: 2A / 2B. */
function frenchDepartmentCodeFromPostal(postalCode: string | null | undefined): string | null {
  if (!postalCode) return null;
  const digits = postalCode.replace(/\s/g, '');
  if (!/^\d{5}$/.test(digits)) return null;
  const n = Number.parseInt(digits, 10);
  const d2 = digits.slice(0, 2);
  if (d2 === '97' || d2 === '98') return digits.slice(0, 3);
  if (d2 === '20') {
    return n <= 20199 ? '2A' : '2B';
  }
  return d2;
}

/** Return a French department code when `raw` looks like 2–3 digits or 2A/2B. */
function looksLikeFrenchDeptCode(raw: string): string | null {
  const t = raw.trim();
  if (/^\d{2,3}$/.test(t)) return t;
  if (/^2[AB]$/i.test(t)) return t.toUpperCase();
  return null;
}

/** True when the stored country is France (`France` or leftover `FR`). */
function isFranceAddressCountry(country: string | null | undefined): boolean {
  const raw = (country ?? 'France').trim().toLowerCase();
  return raw === 'france' || raw === 'fr';
}

/** Department code for an establishment label: French postal first, then stored department. */
function resolveDepartmentCodeForLabel(e: Establishment): string | null {
  if (isFranceAddressCountry(e.country)) {
    const fromPostal = frenchDepartmentCodeFromPostal(e.postalCode);
    if (fromPostal) return fromPostal;
  }
  if (e.department) return looksLikeFrenchDeptCode(e.department);
  return null;
}

/** Hint shown under every establishment search field. */
export const ESTABLISHMENT_SEARCH_HINT =
  "Tapez le nom de l'établissement. Pour filtrer par département, ajoutez-le entre parenthèses : Jules (50) pour le code département, Jules (Seine) pour le nom.";

/**
 * Builds a minimal establishment object for autocomplete preselection in edit mode.
 * Uses the loaded detail first, then the denormalized name from session/group data.
 */
export function stubEstablishmentForSelect(
  establishmentId: string | undefined,
  detail: Pick<Establishment, 'id' | 'name'> | null | undefined,
  denormalizedName?: string | null,
): Pick<Establishment, 'id' | 'name'> | undefined {
  if (!establishmentId) return undefined;
  if (detail?.id === establishmentId) return detail;
  const name = denormalizedName?.trim();
  if (name) return { id: establishmentId, name };
  return undefined;
}

/** Label for selects/autocomplete: `Name (City - deptCode)` when city + code available, else name. */
export function formatEstablishmentSelectLabel(
  e: Pick<Establishment, 'name'> &
    Partial<Pick<Establishment, 'city' | 'department' | 'postalCode' | 'country'>>,
): string {
  const deptCode = resolveDepartmentCodeForLabel(e as Establishment);
  return e.city && deptCode ? `${e.name} (${e.city} - ${deptCode})` : e.name;
}
